import { Box, Typography } from "@mui/material";
import type { Customer } from "../types";

interface MembershipCardProps {
  user: Customer;
  expiringPoints?: unknown;
  membershipCardImage?: string;
  showQR?: boolean;
  onQrCodeClick?: () => void;
  showPoints?: boolean;
  showMembershipLevel?: boolean;
  showQrOnlyOnClick?: boolean;
}

export default function MembershipCard({
  user,
  showQR = true,
  showMembershipLevel = true,
  showPoints = true,
  showQrOnlyOnClick = false,
  onQrCodeClick,
}: MembershipCardProps) {
  const handleQrCodeClick = () => {
    if (onQrCodeClick) onQrCodeClick();
  };

  return (
    <Box
      onClick={showQR && !showQrOnlyOnClick ? handleQrCodeClick : undefined}
      sx={{
        position: "relative",
        width: "calc(100% - 36px)",
        mx: "auto",
        cursor: showQR && !showQrOnlyOnClick ? "pointer" : "default",
        borderRadius: "14px",
        overflow: "hidden",
        height: "118px",
        padding: "14px 16px 12px",
        color: "#fff",
        background:
          "linear-gradient(135deg, #E85A22 0%, #F27A3A 38%, #F5A06A 62%, #F8C9A8 100%)",
        boxShadow: "0 6px 18px rgba(232, 90, 34, 0.24)",
      }}
    >
      {/* Diagonal sheen overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(118deg, transparent 0%, transparent 46%, rgba(255,255,255,0.12) 46.2%, rgba(255,230,200,0.35) 70%, rgba(255,240,220,0.5) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Decorative circle */}
      <Box
        sx={{
          position: "absolute",
          right: "-20px",
          bottom: "-40px",
          width: "160px",
          height: "160px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          pointerEvents: "none",
        }}
      />

      {/* Top row: tier name + QR button */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        {showMembershipLevel && (
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              letterSpacing: "0.2px",
              textShadow: "0 1px 2px rgba(0,0,0,0.12)",
              color: "#fff",
            }}
          >
            {user?.nextMembershipLevel?.currentMembershipLevelName}
          </Typography>
        )}

        {showQR && (
          <Box
            data-carousel-ignore
            onClick={handleQrCodeClick}
            onPointerDown={(e) => e.stopPropagation()}
            sx={{
              width: "36px",
              height: "36px",
              background: "#fff",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
          >
            <svg width="26" height="26" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
                <rect x="5" y="5" width="6" height="6" fill="#111"/>
                <rect x="20" y="2" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
                <rect x="23" y="5" width="6" height="6" fill="#111"/>
                <rect x="2" y="20" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
                <rect x="5" y="23" width="6" height="6" fill="#111"/>
                <rect x="18" y="18" width="4" height="4" fill="#111"/>
                <rect x="24" y="18" width="4" height="4" fill="#111"/>
                <rect x="18" y="24" width="4" height="4" fill="#111"/>
                <rect x="28" y="22" width="4" height="4" fill="#111"/>
                <rect x="22" y="28" width="4" height="4" fill="#111"/>
                <rect x="28" y="28" width="4" height="4" fill="#111"/>
              </svg>
          </Box>
        )}
      </Box>

      {/* Points balance at bottom */}
      {showPoints && (
        <Box
          sx={{
            position: "absolute",
            left: "16px",
            bottom: "12px",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              letterSpacing: "-0.4px",
              lineHeight: 1,
              textShadow: "0 1px 3px rgba(0,0,0,0.15)",
              color: "#fff",
            }}
          >
            {user?.pointsBalance?.toLocaleString() ?? "0"}
          </Typography>

        </Box>
      )}
    </Box>
  );
}
